import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getYear, getMonth } from 'date-fns';
import { ChevronDownIcon, PlusIcon } from '@heroicons/react/24/solid';
import { fetchSavingsBudgets, Budget, checkUnreviewedEOM, ignoreEOM } from '../api/budgets';
import LoadingSpinner from '../components/LoadingSpinner';
import { QuickAddOverlay } from '../components/QuickAddOverlay';
import { EOMBanner } from '../components/EOMBanner';
import { EOMOverlay } from '../components/EOMOverlay';

const currentYear = getYear(new Date());
const currentMonth = getMonth(new Date());
const years = [currentYear, ...Array.from({ length: 10 }, (_, i) => currentYear - i - 1)];
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function NativePillSelect({ 
  value, 
  options, 
  onChange, 
  className = "" 
}: { 
  value: string; 
  options: string[]; 
  onChange: (v: string) => void;
  className?: string;
}) {
  return (
    <div className={`relative flex-1 ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-gray-200 rounded-full px-6 py-2.5 text-base font-semibold text-gray-800 focus:outline-none select-none text-center cursor-pointer"
        style={{
          backgroundImage: 'none',
          paddingRight: '2.5rem',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          touchAction: 'manipulation'
        }}
      >
        {options.map(option => (
          <option key={option} value={option} className="text-left py-2 px-4">
            {option}
          </option>
        ))}
      </select>
      <ChevronDownIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none" />
    </div>
  );
}

function SavingsBudgetItem({ 
  budget, 
  onAddClick, 
  selectedYear, 
  selectedMonth,
  onBudgetClick
}: { 
  budget: Budget; 
  onAddClick: (budget: Budget) => void;
  selectedYear?: number;
  selectedMonth?: number;
  onBudgetClick?: () => void;
}) {
  const navigate = useNavigate();
  
  // Use the data from the API response
  const totalSaved = budget.spent || 0; // This is now total saved (savings - expenses)
  const targetAmount = budget.amount;
  const percentageSaved = targetAmount > 0 ? (totalSaved / targetAmount) * 100 : 0;
  
  // Get monthly data from API response
  const monthlySaved = (budget as any).monthlySaved || 0;
  const monthlyGoal = (budget as any).monthlyGoal || targetAmount;

  // Render icon - handle both Font Awesome classes and emoji
  const renderIcon = () => {
    if (budget.icon.startsWith('fas fa-') || budget.icon.startsWith('far fa-') || budget.icon.startsWith('fab fa-')) {
      return <i className={budget.icon}></i>;
    }
    return budget.icon;
  };

  const handleBudgetClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking the add button
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }

    // Save scroll position before navigating
    if (onBudgetClick) {
      onBudgetClick();
    }

    // Build URL with year and month parameters if provided
    let url = `/budget/${budget._id}`;
    const params = new URLSearchParams();
    if (selectedYear !== undefined) {
      params.set('year', selectedYear.toString());
    }
    if (selectedMonth !== undefined) {
      params.set('month', selectedMonth.toString());
    }
    // Add source parameter to track where we came from
    params.set('source', 'savings');
    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    navigate(url);
  };

  return (
    <div 
      className="bg-white rounded-xl shadow relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
      onClick={handleBudgetClick}
    >
      <div className="flex items-center p-4 pb-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F8F9FA] mr-4">
          <div className="text-sm">{renderIcon()}</div>
        </div>
        <div className="flex-1">
          <div className="font-semibold text-lg">{budget.name}</div>
          
          {/* Overall Progress */}
          {budget.savingsGoal === 'Target Goal' && (
            <div>
               {(() => {
                 const leftToSave = monthlyGoal - monthlySaved;
                 const needsSaving = leftToSave > 0.01; // Use small threshold to avoid floating point precision issues
                 return (
                   <div className={`text-base ${needsSaving ? 'font-semibold text-emerald-700' : 'text-gray-600'}`}>
                     <span className={needsSaving ? 'font-semibold text-emerald-700' : 'text-gray-600'}>${Math.max(0, leftToSave).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className={needsSaving ? 'text-emerald-700' : 'text-gray-500 font-normal'}>left to save this month</span>
                   </div>
                 );
               })()}
               <div className="text-base mb-1">
                 <span className="font-semibold text-gray-600">${totalSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className="text-gray-500 font-normal">of ${targetAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved overall</span>
               </div>
               <div className="text-base text-gray-600">
                 <span className="font-semibold text-gray-600">${(monthlyGoal - monthlySaved).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className="text-gray-500 font-normal">left to hit goal</span>
               </div>
            </div>
          )}
          {budget.savingsGoal === 'Monthly Goal' && (
            <div>
               {(() => {
                 const leftToSave = monthlyGoal - monthlySaved;
                 const needsSaving = leftToSave > 0.01; // Use small threshold to avoid floating point precision issues
                 return (
                   <div className={`text-base mb-1 ${needsSaving ? 'font-semibold text-emerald-700' : 'text-gray-600'}`}>
                     <span className={needsSaving ? 'font-semibold text-emerald-700' : 'text-gray-600'}>${Math.max(0, leftToSave).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className={needsSaving ? 'text-emerald-700' : 'text-gray-500 font-normal'}>left to save this month</span>
                   </div>
                 );
               })()}
               <div className="text-base mb-1">
                 <span className="font-semibold text-gray-600">${totalSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className="text-gray-500 font-normal">saved overall</span>
               </div>
               <div className="text-base text-gray-600">
                 <span className="font-semibold text-gray-600">${totalSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className="text-gray-500 font-normal">of ${targetAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved</span>
               </div>
            </div>
          )}

          {budget.budgetType === 'Expense Fund' && (
            <div>
               {(() => {
                 const leftToSave = monthlyGoal - monthlySaved;
                 const needsSaving = leftToSave > 0.01; // Use small threshold to avoid floating point precision issues
                 return (
                   <div className={`text-base mb-1 ${needsSaving ? 'font-semibold text-emerald-700' : 'text-gray-600'}`}>
                     <span className={needsSaving ? 'font-semibold text-emerald-700' : 'text-gray-600'}>${Math.max(0, leftToSave).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className={needsSaving ? 'text-emerald-700' : 'text-gray-500 font-normal'}>left to save this month</span>
                   </div>
                 );
               })()}
               <div className="text-base mb-1">
                 <span className="font-semibold text-gray-600">${(((budget as any).savingsAmount || 0) - ((budget as any).expenseAmount || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> <span className="text-gray-500 font-normal">saved overall</span>
               </div>
               <div className="text-base text-gray-600">
                 {(() => {
                   const netSaved = ((budget as any).savingsAmount || 0) - ((budget as any).expenseAmount || 0);
                   const totalPeriodAmount = budget.amount;
                   if (netSaved < 0) {
                     return (
                       <span className="font-semibold text-red-600">
                         Under funded by ${Math.abs(netSaved).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                       </span>
                     );
                   } else {
                     return (
                       <span className="font-semibold text-gray-600">
                         ${netSaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of ${totalPeriodAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved
                       </span>
                     );
                   }
                 })()}
               </div>
            </div>
          )}
          
          
        </div>
        <button
          className="ml-4 flex items-center justify-center w-12 h-12 rounded-full bg-[#F8F9FA] hover:bg-gray-200 transition"
          onClick={(e) => {
            e.stopPropagation();
            onAddClick(budget);
          }}
          aria-label={`Add transaction to ${budget.name}`}
        >
          <PlusIcon className="w-7 h-7 text-black" />
        </button>
      </div>
      
      {/* Progress Bar */}
      <div className="h-1.5 bg-gray-300 relative">
        <div
          className="h-full bg-green-500 transition-all"
          style={{ 
            width: `${Math.min(100, Math.max(0, percentageSaved))}%`,
            position: 'absolute',
            left: '0',
            top: '0',
            borderRadius: '3px 3px 3px 3px'
          }}
        />
      </div>
    </div>
  );
}

export function Savings() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get year and month from URL parameters, fallback to current date
  const urlYear = searchParams.get('year');
  const urlMonth = searchParams.get('month');
  const initialYear = urlYear ? parseInt(urlYear) : currentYear;
  const initialMonth = urlMonth ? parseInt(urlMonth) : currentMonth;

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);

  // Update URL params when year or month changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('year', year.toString());
    params.set('month', month.toString());
    setSearchParams(params, { replace: true });
  }, [year, month, searchParams, setSearchParams]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickAddOverlay, setShowQuickAddOverlay] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showEOMBanner, setShowEOMBanner] = useState(false);
  const [eomYear, setEomYear] = useState<number | null>(null);
  const [eomMonth, setEomMonth] = useState<number | null>(null);
  const [showEOMOverlay, setShowEOMOverlay] = useState(false);

  // Load savings budgets for the selected year/month
  const loadSavingsBudgets = useCallback(async (selectedYear: number, selectedMonth: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const fetchedBudgets = await fetchSavingsBudgets(selectedYear, selectedMonth);
      setBudgets(fetchedBudgets);
      
    } catch (err: any) {
      setError(err.message || 'Failed to load savings budgets');
    } finally {
      setLoading(false);
    }
  }, []);

  // Restore scroll position when component mounts or data loads
  useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      const savedScrollPosition = sessionStorage.getItem('savingsScrollPosition');
      if (savedScrollPosition) {
        scrollContainerRef.current.scrollTop = parseInt(savedScrollPosition, 10);
        sessionStorage.removeItem('savingsScrollPosition');
      }
    }
  }, [loading]);

  // Save scroll position when navigating away or opening overlay
  const saveScrollPosition = () => {
    if (scrollContainerRef.current) {
      sessionStorage.setItem('savingsScrollPosition', scrollContainerRef.current.scrollTop.toString());
    }
  };

  // Check for unreviewed EOM
  const checkEOM = useCallback(async () => {
    try {
      const eomCheck = await checkUnreviewedEOM();
      if (eomCheck.hasUnreviewedEOM && eomCheck.year !== undefined && eomCheck.month !== undefined) {
        setShowEOMBanner(true);
        setEomYear(eomCheck.year);
        setEomMonth(eomCheck.month);
      } else {
        setShowEOMBanner(false);
      }
    } catch (err) {
      // Silently fail - don't show banner if check fails
      setShowEOMBanner(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadSavingsBudgets(year, month);
    checkEOM();
  }, [loadSavingsBudgets, year, month, checkEOM]);

  // Reload data when year or month changes
  useEffect(() => {
    loadSavingsBudgets(year, month);
  }, [year, month, loadSavingsBudgets]);

  // Filter budgets based on search term
  const filteredBudgets = budgets.filter(budget =>
    budget.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate budgets into Expense Funds and Savings, and sort alphabetically
  const expenseFundBudgets = filteredBudgets
    .filter(budget => budget.budgetType === 'Expense Fund')
    .sort((a, b) => a.name.localeCompare(b.name));
  
  const savingsBudgets = filteredBudgets
    .filter(budget => budget.budgetType === 'Savings' || !budget.budgetType)
    .sort((a, b) => a.name.localeCompare(b.name));

  // Calculate total left to save this month for each category
  const expenseFundsLeftToSave = expenseFundBudgets.reduce((total, budget) => {
    const monthlyGoal = budget.expensePeriodValue && budget.expensePeriodUnit
      ? (budget.expensePeriodUnit === 'Years' 
          ? budget.amount / (budget.expensePeriodValue * 12)
          : budget.amount / budget.expensePeriodValue)
      : budget.amount;
    const monthlySaved = (budget as any).monthlySaved || 0;
    return total + Math.max(0, monthlyGoal - monthlySaved);
  }, 0);

  const savingsLeftToSave = savingsBudgets.reduce((total, budget) => {
    const monthlyGoal = budget.amount;
    const monthlySaved = (budget as any).monthlySaved || 0;
    return total + Math.max(0, monthlyGoal - monthlySaved);
  }, 0);

  // Calculate monthly savings progress
  const calculateMonthlySavingsProgress = () => {
    let totalMonthlyGoal = 0;
    let totalMonthlySaved = 0;

    budgets.forEach(budget => {
      if (budget.budgetType === 'Savings') {
        // For savings budgets, use the monthly goal directly
        totalMonthlyGoal += budget.amount;
        totalMonthlySaved += (budget as any).monthlySaved || 0;
      } else if (budget.budgetType === 'Expense Fund') {
        // For expense fund budgets, calculate the monthly amount needed
        const periodValue = budget.expensePeriodValue || 1;
        const periodUnit = budget.expensePeriodUnit || 'Months';
        const monthlyAmount = periodUnit === 'Years' 
          ? budget.amount / (periodValue * 12)
          : budget.amount / periodValue;
        
        totalMonthlyGoal += monthlyAmount;
        totalMonthlySaved += (budget as any).monthlySaved || 0;
      }
    });

    const remaining = Math.max(0, totalMonthlyGoal - totalMonthlySaved);
    const percentageSaved = totalMonthlyGoal > 0 ? (totalMonthlySaved / totalMonthlyGoal) * 100 : 0;
    const percentageRemaining = totalMonthlyGoal > 0 ? (remaining / totalMonthlyGoal) * 100 : 0;

    return {
      totalMonthlyGoal,
      totalMonthlySaved,
      remaining,
      percentageSaved,
      percentageRemaining
    };
  };

  const monthlyProgress = calculateMonthlySavingsProgress();

  // Handle add transaction click
  const handleAddClick = (budget: Budget) => {
    saveScrollPosition();
    setSelectedBudget(budget);
    setShowQuickAddOverlay(true);
  };

  const handleIgnoreEOM = async () => {
    if (eomYear !== null && eomMonth !== null) {
      try {
        await ignoreEOM(eomYear, eomMonth);
        setShowEOMBanner(false);
      } catch (err) {
        // Handle error silently
      }
    }
  };

  const handleViewEOM = () => {
    setShowEOMOverlay(true);
  };

  const handleEOMOverlayClose = () => {
    setShowEOMOverlay(false);
    checkEOM();
    loadSavingsBudgets(year, month);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center">
        <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <LoadingSpinner size="lg" />
              <p className="text-gray-500 text-sm">Loading savings goals...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center">
        <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <p className="text-red-500 text-sm mb-2">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center">
      <div className="w-full max-w-4xl mx-auto flex flex-col min-h-screen h-screen">
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-center pt-4 pb-2 px-4 mb-4">
          <div className="flex-1 flex flex-col items-center">
            <img
              src="/budgie_logo_sm.png"
              alt="Budgie Logo"
              className="h-10 object-contain mx-auto"
              style={{ maxWidth: 100 }}
            />
          </div>
        </div>

        {/* Dropdowns */}
        <div className="flex-shrink-0 flex gap-2 px-6 mb-6">
          <NativePillSelect
            value={year.toString()}
            options={years.map(y => y.toString())}
            onChange={v => setYear(Number(v))}
          />
          <NativePillSelect
            value={months[month]}
            options={months}
            onChange={v => setMonth(months.indexOf(v))}
          />
        </div>
        {/* EOM Banner */}
        {showEOMBanner && eomYear !== null && eomMonth !== null && (
          <EOMBanner
            year={eomYear}
            month={eomMonth}
            onView={handleViewEOM}
            onIgnore={handleIgnoreEOM}
          />
        )}

        {/* Monthly Savings Progress */}
        <div className="flex-shrink-0 px-6 mb-6">
          <div className="flex justify-between items-end">
            <div className="text-lg text-gray-600 font-medium">Monthly Savings</div>
            <div className="text-base font-semibold text-gray-600">
              ${monthlyProgress.remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left to save
            </div>
          </div>
          <div className="w-full h-2 bg-gray-300 rounded-full mt-2 overflow-hidden">
            <div
              className="h-2 rounded-full transition-all ease-out bg-green-500"
              style={{ 
                width: `${Math.min(100, Math.max(0, monthlyProgress.percentageSaved))}%`,
                left: '0',
                transitionDuration: '300ms'
              }}
            />
          </div>
          {/* Progress Text */}
          {/* <div className="mt-2 text-xs text-gray-500 text-center">
            ${monthlyProgress.totalMonthlySaved.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} of ${monthlyProgress.totalMonthlyGoal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} saved this month
          </div> */}
        </div>

        {/* Savings Goals List */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 min-h-0 overflow-y-auto"
        >
          <div className="flex flex-col gap-3 px-6 pb-28 pt-6 bg-[#F8F9FA] mb-4 rounded-xl">
            {/* Search Input */}
            <div className="relative mb-3">
              <input
                type="text"
                placeholder="Search savings goals..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white rounded-full px-6 py-2.5 text-base font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all shadow-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
            {budgets.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-gray-500 text-sm mb-2">No savings goals found</p>
                  <p className="text-gray-400 text-xs">Create savings budgets to track your goals</p>
                </div>
              </div>
            ) : filteredBudgets.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <p className="text-gray-500 text-sm mb-2">
                    {searchTerm ? 'No savings goals match your search' : 'No savings goals found'}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {searchTerm ? 'Try adjusting your search terms' : 'Create savings budgets to track your goals'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-6">
                {/* Expense Funds Section */}
                {expenseFundBudgets.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between px-2">
                      <div className="text-sm font-semibold text-gray-600">
                        Expense Funds
                      </div>
                      <div className="text-sm font-semibold text-gray-600">
                        ${expenseFundsLeftToSave.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left to save
                      </div>
                    </div>
                    {expenseFundBudgets.map(budget => (
                      <SavingsBudgetItem 
                        key={budget._id} 
                        budget={budget}
                        onAddClick={handleAddClick}
                        selectedYear={year}
                        selectedMonth={month}
                        onBudgetClick={saveScrollPosition}
                      />
                    ))}
                  </div>
                )}
                
                {/* Savings Section */}
                {savingsBudgets.length > 0 && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between px-2">
                      <div className="text-sm font-semibold text-gray-600">
                        Savings
                      </div>
                      <div className="text-sm font-semibold text-gray-600">
                        ${savingsLeftToSave.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} left to save
                      </div>
                    </div>
                    {savingsBudgets.map(budget => (
                      <SavingsBudgetItem 
                        key={budget._id} 
                        budget={budget}
                        onAddClick={handleAddClick}
                        selectedYear={year}
                        selectedMonth={month}
                        onBudgetClick={saveScrollPosition}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Add Overlay */}
      {showQuickAddOverlay && selectedBudget && (
        <QuickAddOverlay
          budgets={budgets.map(budget => ({
            id: budget._id,
            name: budget.name,
            icon: budget.icon,
            amount: budget.amount,
            spent: budget.spent || 0,
            budgetType: budget.budgetType,
            savingsGoal: budget.savingsGoal,
            targetDate: budget.targetDate,
            expensePeriodValue: budget.expensePeriodValue,
            expensePeriodUnit: budget.expensePeriodUnit
          }))}
          initialBudget={{
            id: selectedBudget._id,
            name: selectedBudget.name,
            icon: selectedBudget.icon,
            amount: selectedBudget.amount,
            spent: selectedBudget.spent || 0,
            budgetType: selectedBudget.budgetType,
            savingsGoal: selectedBudget.savingsGoal,
            targetDate: selectedBudget.targetDate,
            expensePeriodValue: selectedBudget.expensePeriodValue,
            expensePeriodUnit: selectedBudget.expensePeriodUnit
          }}
          onClose={() => {
            setShowQuickAddOverlay(false);
            setSelectedBudget(null);
          }}
          onTransactionCreated={() => {
            setShowQuickAddOverlay(false);
            setSelectedBudget(null);
            // Reload budgets to show updated amounts
            loadSavingsBudgets(year, month);
          }}
          transactionType="savings"
          selectedYear={year}
          selectedMonth={month}
        />
      )}
      {/* EOM Overlay */}
      {showEOMOverlay && eomYear !== null && eomMonth !== null && (
        <EOMOverlay
          year={eomYear}
          month={eomMonth}
          onClose={handleEOMOverlayClose}
          onDataUpdated={() => {
            loadSavingsBudgets(year, month);
          }}
        />
      )}
    </div>
  );
}
